import React from 'react';
import { Document } from '@contentful/rich-text-types';
import { RichText } from '@/components';
import { isValidHtmlString } from '@/utils/strings/htmlValidator';

interface ConditionalRichTextProps {
  content: Document;
  classes?: string;
  addParagraphMargins?: boolean;
  openLinkSameWindow?: boolean;
  removeHoverEffect?: boolean;
  templateData?: Record<string, any>;
  'data-testid'?: string;
}

// Helper function to extract text content from RichText Document
export const extractTextFromRichText = (document: Document): string => {
  if (!document?.content) return '';

  return document.content
    .map((block: any) => {
      if (block.content) {
        return block.content
          .map((node: any) => {
            if (node.nodeType === 'text' && node.value) {
              return node.value;
            }
            return '';
          })
          .join('');
      }
      return '';
    })
    .join(' ');
};

/**
 * ConditionalRichText component that automatically decides between rendering
 * HTML content with dangerouslySetInnerHTML or using the RichText component
 * based on whether the content contains valid HTML tags.
 */
export const ConditionalRichText: React.FC<ConditionalRichTextProps> = ({
  content,
  classes = '',
  addParagraphMargins = true,
  openLinkSameWindow = false,
  removeHoverEffect = false,
  templateData = {},
  'data-testid': dataTestId,
}) => {
  const textContent = extractTextFromRichText(content);
  const isHtml = isValidHtmlString(textContent);

  if (isHtml) {
    return (
      <p
        className={classes}
        dangerouslySetInnerHTML={{ __html: textContent }}
        data-testid={dataTestId}
      />
    );
  }

  return (
    <div data-testid={dataTestId} className={classes}>
      <RichText
        content={content}
        addParagraphMargins={addParagraphMargins}
        openLinkSameWindow={openLinkSameWindow}
        removeHoverEffect={removeHoverEffect}
        templateData={templateData}
      />
    </div>
  );
};
